import React, { useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  ChecklistItem, TripRequest, TripRequestStatus,
} from '@marageti/z4-sdk/lib/travel';
import {
  useFetchImmutable,
  useApiClient,
  useFetch,
} from '@marageti/z4-lib';
import { useSnackbar } from 'notistack';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import { useConfirm } from 'material-ui-confirm';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { Traveler } from '@marageti/z4-sdk/lib/people';
import { formatCountryCodes } from '../../utils/helpers';
import TripRequestSecondaryAppbarView from './trip-request-sub-navigation-view';
import { useAuthStore } from '../../store';

export const UNREAD_COUNT_PARAMS = { hasNewOnly: true, limit: 0 };

const isInStatus = (status: TripRequestStatus | undefined, statuses: TripRequestStatus[]): boolean => status !== undefined && statuses.includes(status);

const disableReferralStatuses = [
  TripRequestStatus.PendingStatus,
  TripRequestStatus.JunkStatus,
  TripRequestStatus.CanceledStatus,
  TripRequestStatus.ReferredStatus,
  TripRequestStatus.MatchedStatus,
  TripRequestStatus.PostponedStatus,
  TripRequestStatus.SoldStatus,
];

const cancellableStatuses = [
  TripRequestStatus.PendingStatus,
  TripRequestStatus.VerifiedStatus,
  TripRequestStatus.MatchedStatus,
  TripRequestStatus.SoldStatus,
  TripRequestStatus.ReferredStatus,
  TripRequestStatus.PostponedStatus,
];

const junkableStatuses = [TripRequestStatus.PendingStatus, TripRequestStatus.VerifiedStatus, TripRequestStatus.JunkStatus];

const TripRequestSubNavigationContainer = () => {
  const apiClient = useApiClient();
  const { tripRequestId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { loggedInTeamMember } = useAuthStore();

  const { data: countryCodes } = useFetchImmutable<CountryCodeMap>(apiClient.standardsClient.getCountryCodes, [], true);
  const confirm = useConfirm();
  const { enqueueSnackbar } = useSnackbar();

  const { data: tripRequest, mutate: mutateTripRequest } = useFetch<TripRequest>(
    apiClient.tripRequestsClient.getTripRequest,
    [tripRequestId],
    !!tripRequestId,
  );

  const { data: tripMatches, mutate: mutateTripMatches } = useFetch<TripRequest[]>(
    apiClient.tripRequestsClient.getTripRequestTripMatches,
    [tripRequestId],
    !!tripRequestId,
  );

  const { data: traveler, mutate: mutateTraveler } = useFetch<Traveler>(
    apiClient.peopleClient.getTraveler,
    [tripRequest?.travelers[0]?.id],
    !!tripRequest?.travelers[0]?.id,
  );

  const countries = tripRequest?.countries && countryCodes ? formatCountryCodes(tripRequest.countries, countryCodes) : undefined;
  const isFollowing = loggedInTeamMember && tripRequest ? tripRequest.followers?.includes(loggedInTeamMember.id) : false;
  const isAssignedToUser = tripRequest?.assignedTo === loggedInTeamMember?.id;
  const isJunk = tripRequest?.status === TripRequestStatus.JunkStatus;
  const isSold = tripRequest?.status === TripRequestStatus.SoldStatus;
  const hasSoldTripMatches = tripMatches?.some((tm) => tm.status === TripRequestStatus.SoldStatus);

  const postponableStatuses = [TripRequestStatus.PendingStatus, TripRequestStatus.VerifiedStatus];
  const isPostponeable = isInStatus(tripRequest?.status, postponableStatuses);
  const isCancellable = isSold ? !hasSoldTripMatches : isInStatus(tripRequest?.status, cancellableStatuses);
  const isReferrable = !isInStatus(tripRequest?.status, disableReferralStatuses);
  const isJunkable = isInStatus(tripRequest?.status, junkableStatuses);

  const onClickFollow = async () => {
    try {
      if (tripRequestId) {
        if (isFollowing) {
          await apiClient.tripRequestsClient.deleteTripRequestFollower(tripRequestId);
          enqueueSnackbar({
            message: 'Unfollowed trip request.',
            variant: 'success',
          });
        } else {
          await apiClient.tripRequestsClient.postTripRequestFollower(tripRequestId);
          enqueueSnackbar({
            message: 'Following trip request.',
            variant: 'success',
          });
        }

        mutateTripRequest();
      }
    } catch (error) {
      console.log(error);
    }
  };

  const onClickDismiss = async () => {
    try {
      await confirm({
        title: 'Dismiss from Queue',
        description: (
          <>
            Completing this action removes the request from your assigned queue, indicating that you no longer take any action on it.
            <br />
            <br />
            If you are currently following or have postponed this request, those flags will be removed. New messages will still display notifications, and you can also find it in the All queue.
          </>
        ),
        confirmationText: 'Continue',
      });

      if (tripRequestId && tripRequest) {
        if (tripRequest.status === TripRequestStatus.PostponedStatus && tripRequest.statusInfo.lastStatus) {
          await apiClient.tripRequestsClient.putTripRequestStatus(tripRequestId!, {
            id: tripRequestId,
            status: tripRequest.statusInfo.lastStatus,
          });
        }
        await apiClient.tripRequestsClient.deleteTripRequestChecklistItems(tripRequestId!, {
          params: { item: [ChecklistItem.NeedsMatching, ChecklistItem.NeedsScreening] },
        });
      }

      await mutateTripRequest();
      enqueueSnackbar({
        message: 'Trip request dismissed.',
        variant: 'success',
      });
      if (location.key === 'default') {
        navigate('/trip-requests/my-queue');
      } else {
        navigate(-1);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const onClickPostpone = async () => {
    try {
      await confirm({
        title: 'Postpone Trip?',
        description: (
          <>
            Postponing this trip will move it to the
            {' '}
            <em>Postpone</em>
            {' '}
            section of your queue. It can be reactivated at a later date.
          </>
        ),
        confirmationText: 'Postpone',
        confirmationButtonProps: {
          variant: 'contained',
          color: 'primary',
        },
      });

      if (tripRequestId) {
        await apiClient.tripRequestsClient.putTripRequestStatus(tripRequestId, {
          id: tripRequestId,
          status: TripRequestStatus.PostponedStatus,
        });
      }

      mutateTripRequest();
    } catch (error) {
      console.log(error);
    }
  };

  const onClickCancel = () => {
    confirm({
      title: isJunk ? 'Not Junk' : 'Cancel Request',
      description: isJunk
        ? 'This will remove this request from Junk.'
        : 'This will cancel the request for the traveler and remove it from the assignee\'s queue.',
      confirmationButtonProps: {
        variant: 'contained',
        color: 'error',
      },
    })
      .then(() => apiClient.tripRequestsClient.putTripRequestStatus(tripRequestId!, {
        id: tripRequestId!,
        status: isJunk ? TripRequestStatus.VerifiedStatus : TripRequestStatus.CanceledStatus,
        statusInfo: {
          reason: 'Canceled by Zicasso',
        },
      }))
      .then(() => {
        mutateTraveler();
        mutateTripRequest();
        mutateTripMatches();
        enqueueSnackbar(isJunk ? 'Removed this request from Junk.' : 'This request has been canceled.', { variant: 'success' });
      })
      .catch((e: ApiError) => {
        if (e !== undefined) enqueueSnackbar('There was an error canceling this request.', { variant: 'default' });
      });
  };

  const onClickJunk = () => {
    const description = isJunk
      ? 'This will remove this request from Junk.'
      : (
        <>
          Continuing with this action means Zicasso will never service this trip. It will still be available in the
          <em>All</em>
          {' '}
          queue.
        </>
      );
    confirm({
      title: isJunk ? 'Not Junk' : 'Junk Trip?',
      description,
      confirmationText: 'Junk',
      confirmationButtonProps: {
        variant: 'contained',
        color: 'error',
      },
    })
      .then(() => apiClient.tripRequestsClient.putTripRequestStatus(tripRequestId!, {
        id: tripRequestId!,
        status: isJunk ? TripRequestStatus.VerifiedStatus : TripRequestStatus.JunkStatus,
      }))
      .then(() => {
        mutateTraveler();
        mutateTripRequest();
        enqueueSnackbar(isJunk ? 'Removed this request from Junk.' : 'Trip junked.', { variant: 'success' });
      })
      .catch((e: ApiError) => {
        if (e !== undefined) enqueueSnackbar('There was an error junking this request.', { variant: 'default' });
      });
  };

  const contextMenuItems = useMemo(
    () => [
      {
        action: onClickFollow,
        label: isFollowing ? 'Unfollow' : 'Follow',
      },
      isAssignedToUser && {
        action: onClickDismiss,
        label: 'Dismiss',
      },
      isPostponeable && {
        action: onClickPostpone,
        label: 'Postpone',
      },
      isReferrable && {
        label: 'Referral',
        action: () => navigate(`/trip-requests/${tripRequestId}/referral`),
      },
      isCancellable && {
        action: onClickCancel,
        label: 'Cancel Request',
      },
      isJunkable && {
        action: onClickJunk,
        label: isJunk ? 'Not Junk' : 'Junk',
      },
    ].filter(Boolean) as { action: () => void; label: string }[],
    [
      isFollowing,
      isAssignedToUser,
      isPostponeable,
      isCancellable,
      isJunkable,
      isJunk,
      onClickFollow,
      onClickDismiss,
      onClickPostpone,
      onClickCancel,
      onClickJunk,
    ],
  );

  return (
    <TripRequestSecondaryAppbarView
      countries={countries}
      tripRequest={tripRequest}
      traveler={traveler}
      contextMenuItems={contextMenuItems}
    />
  );
};

export default TripRequestSubNavigationContainer;
